import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NotificationService } from 'src/app/core/services/notification.service';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

import { RegistrationService } from '../../services/registration.service';
import { EmailValidationState } from '../../models/registration.models';
import { ProfessionalEmailValidator } from '../../validators/professional-email.validator';
import {
  AccountData,
  CompanyConflictData,
  TargetCompany,
} from './register-create/register-create.component';
import { LEGAL_MENTION_URLS } from 'src/app/constants/document-urls';

enum ViewState {
  ACCOUNT = 'account',
  CREATE = 'create',
  JOIN = 'join',
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  standalone: false,
})
export class RegisterComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Services
  private registrationService = inject(RegistrationService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);

  // State
  currentView: ViewState = ViewState.ACCOUNT;
  accountData: AccountData | null = null;
  targetCompany: TargetCompany | null = null;
  emailValidationState: EmailValidationState = {};

  // Expose enum to template
  ViewState = ViewState;

  // Account Form
  accountForm = new FormGroup({
    email: new FormControl('', {
      validators: [
        Validators.required,
        ProfessionalEmailValidator.basicEmailFormat,
      ],
      asyncValidators: [
        ProfessionalEmailValidator.createValidator(this.registrationService),
      ],
      updateOn: 'blur',
    }),
    firstName: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(100),
    ]),
    lastName: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(100),
    ]),
    password: new FormControl('', [
      Validators.required,
      ProfessionalEmailValidator.passwordStrength,
    ]),
    confirmPassword: new FormControl('', [
      Validators.required,
      ProfessionalEmailValidator.matchPassword('password'),
    ]),
    agreedToTerms: new FormControl(false, [Validators.requiredTrue]),
    agreedToPrivacy: new FormControl(false, [Validators.requiredTrue]),
  });

  ngOnInit(): void {
    // Subscribe to email validation state
    this.registrationService
      .getEmailValidationState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.emailValidationState = state;
      });

    // Set up real-time email validation
    this.accountForm
      .get('email')
      ?.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((email) => {
        if (email && email.length > 3) {
          this.registrationService.validateEmailRealTime(email).subscribe();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Event handlers for account step completion
  onAccountStepComplete(): void {
    if (!this.accountForm.valid) {
      this.markFormGroupTouched();
      this.notificationService.warn('Please correct the errors and try again.');
      return;
    }

    // Create account data object
    this.accountData = {
      email: this.accountForm.value.email!,
      password: this.accountForm.value.password!,
      firstName: this.accountForm.value.firstName!,
      lastName: this.accountForm.value.lastName!,
      agreedToTerms: this.accountForm.value.agreedToTerms!,
      agreedToPrivacy: this.accountForm.value.agreedToPrivacy!,
      preferredLanguage: this.translocoService.getActiveLang(),
    };

    // Get current registration state synchronously
    const currentState = this.registrationService.getCurrentRegistrationState();
    const emailCheckResult = currentState.emailCheckResult;

    if (
      emailCheckResult?.companyExists &&
      !emailCheckResult.isDeleted &&
      emailCheckResult.companyId &&
      emailCheckResult.companyName
    ) {
      // Professional email with existing company → Join flow
      this.targetCompany = {
        id: emailCheckResult.companyId,
        name: emailCheckResult.companyName,
      };
      this.currentView = ViewState.JOIN;
    } else {
      // Personal email or professional without existing company → Create flow
      this.currentView = ViewState.CREATE;
    }
  }

  // Event handlers for company conflict
  onCompanyConflict(conflictData: CompanyConflictData): void {
    // User chose to join the similar company
    this.targetCompany = {
      id: conflictData.company.id,
      name: conflictData.company.name,
    };
    this.currentView = ViewState.JOIN;
  }

  // Event handlers for registration completion
  onRegistrationComplete(): void {
    // Registration completed, could navigate or show success message
    // This is handled by the sub-components
  }

  // Template helper methods
  isEmailValid(): boolean {
    return this.emailValidationState.isValid === true;
  }

  isEmailChecking(): boolean {
    return this.emailValidationState.isChecking === true;
  }

  showCompanyExistsMessage(): boolean {
    const currentEmail = this.accountForm.get('email')?.value;
    const currentState = this.registrationService.getCurrentRegistrationState();
    const emailCheckResult = currentState.emailCheckResult;

    // Vérifier que le résultat correspond bien à l'email actuel
    const isCurrentEmailResult =
      emailCheckResult?.checkedEmail === currentEmail;

    return !!(
      this.emailValidationState.emailType === 'professional' &&
      currentEmail &&
      this.isEmailValid() &&
      isCurrentEmailResult &&
      emailCheckResult?.companyExists &&
      !emailCheckResult.isDeleted
    );
  }

  getExistingCompanyName(): string | undefined {
    const currentState = this.registrationService.getCurrentRegistrationState();
    return currentState.emailCheckResult?.companyName;
  }

  isEmailAlreadyTaken(): boolean {
    const currentEmail = this.accountForm.get('email')?.value;
    const currentState = this.registrationService.getCurrentRegistrationState();
    const emailCheckResult = currentState.emailCheckResult;

    // Vérifier que le résultat correspond bien à l'email actuel
    const isCurrentEmailResult =
      emailCheckResult?.checkedEmail === currentEmail;

    return !!(
      currentEmail &&
      isCurrentEmailResult &&
      emailCheckResult?.emailAlreadyExists
    );
  }

  // Helper methods
  private markFormGroupTouched(): void {
    Object.keys(this.accountForm.controls).forEach((key) => {
      const control = this.accountForm.get(key);
      control?.markAsTouched();
    });
  }

  // SSO methods (placeholder for future implementation)
  onGoogleSignIn(): void {
    // TODO: Implement Google SSO
  }

  onMicrosoftSignIn(): void {
    // TODO: Implement Microsoft SSO
  }

  // Terms & Conditions document URLs
    get conditionsUrl(): string {
      return this.resourceUrl(LEGAL_MENTION_URLS.termsAndConditions);
    }
  
    get privacyUrl(): string {
      return this.resourceUrl(LEGAL_MENTION_URLS.privacy);
    }
  
    resourceUrl(urls: Record<string, string>): string {
      const lang = this.translocoService.getActiveLang();
      return urls[lang] ?? urls['en'];
    }
}
